// Shared drag-to-scroll rail (design-system §4) used by Shop-by-Type,
// slat rail (mobile) and UGC rail. Markup contract:
//   <skullette-rail>
//     <div data-rail class="sk-rail">…snap items…</div>
//     <button data-rail-prev>…  <button data-rail-next>…   (optional)
//   </skullette-rail>
// Behaviors: pointer capture, snap off while dragging, release snaps to the
// nearest item (center- or start-aligned per item's scroll-snap-align), flick
// advances when |vx| > 0.5, clicks after >5px drags are suppressed, snap-type
// restores after 440ms. Arrows page by clientWidth and fade at both ends.
// No-JS: the rail is a plain overflow scroller. Reduced motion: no smooth.
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const BEHAVIOR = REDUCED ? 'auto' : 'smooth';

class SkulletteRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-rail]');
    if (!this.rail) return;
    this.prevBtn = this.querySelector('[data-rail-prev]');
    this.nextBtn = this.querySelector('[data-rail-next]');
    this.#attachDrag(this.rail);
    if (!this._arrowsBound && (this.prevBtn || this.nextBtn)) {
      this._arrowsBound = true;
      this.#attachArrows();
    }
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  #attachArrows() {
    const rail = this.rail;
    const page = (dir) => rail.scrollBy({ left: dir * rail.clientWidth, behavior: BEHAVIOR });
    this.prevBtn?.addEventListener('click', () => page(-1));
    this.nextBtn?.addEventListener('click', () => page(1));
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      this.prevBtn?.setAttribute('data-disabled', rail.scrollLeft <= 2 ? 'true' : 'false');
      this.nextBtn?.setAttribute('data-disabled', rail.scrollLeft >= max - 2 ? 'true' : 'false');
    };
    rail.addEventListener('scroll', update, { passive: true });
    this._onResize = update;
    window.addEventListener('resize', update);
    update();
  }

  #attachDrag(rail) {
    // connectedCallback re-runs when the element moves (editor reorder,
    // morph) - without this guard every handler would double up.
    if (rail._dragBound) return;
    rail._dragBound = true;
    let down = false, sx = 0, sl = 0, moved = false, lastX = 0, lastT = 0, vx = 0;
    const targetFor = (k, center) =>
      center ? k.offsetLeft + k.offsetWidth / 2 - rail.clientWidth / 2 : k.offsetLeft;
    rail.addEventListener('dragstart', (e) => e.preventDefault());
    rail.addEventListener('pointerdown', (e) => {
      down = true; moved = false; sx = e.clientX; sl = rail.scrollLeft;
      lastX = e.clientX; lastT = performance.now(); vx = 0;
      rail.style.scrollSnapType = 'none';
      rail.style.cursor = 'grabbing';
    });
    rail.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 5 && !moved) {
        moved = true;
        // Capture only once a REAL drag starts. Capturing on pointerdown
        // retargets mouseup to the rail, which makes the ensuing click's
        // target the rail itself (common-ancestor rule) - child links and
        // the lightbox images would never receive plain clicks.
        try { rail.setPointerCapture(e.pointerId); } catch {}
      }
      rail.scrollLeft = sl - dx;
      const now = performance.now();
      if (now - lastT > 0) vx = (e.clientX - lastX) / (now - lastT);
      lastX = e.clientX; lastT = now;
    });
    const end = (e) => {
      if (!down) return;
      down = false; rail.style.cursor = 'grab';
      try { rail.releasePointerCapture(e.pointerId); } catch {}
      const kids = Array.from(rail.children).filter((k) => k.offsetWidth > 0);
      if (kids.length) {
        const center = /center/.test(getComputedStyle(kids[0]).scrollSnapAlign || '');
        let idx = 0, min = Infinity;
        kids.forEach((k, i) => {
          const d = Math.abs(targetFor(k, center) - rail.scrollLeft);
          if (d < min) { min = d; idx = i; }
        });
        if (vx > 0.5 && idx > 0) idx -= 1;
        else if (vx < -0.5 && idx < kids.length - 1) idx += 1;
        rail.scrollTo({ left: targetFor(kids[idx], center), behavior: BEHAVIOR });
      }
      clearTimeout(rail._snapT);
      rail._snapT = setTimeout(() => { rail.style.scrollSnapType = 'x mandatory'; }, 440);
      setTimeout(() => { moved = false; }, 0);
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  }
}

if (!customElements.get('skullette-rail')) {
  customElements.define('skullette-rail', SkulletteRail);
}
